import React, { useId } from 'react';
import { IdCard, Home, Landmark, Receipt, FileSignature, FileText, Camera, Eye, EyeOff, Download, UploadCloud, RefreshCw, X, CheckCircle2, ShieldCheck } from 'lucide-react';
import ActionButton from './ActionButton';
import { openDocument, downloadDocument } from '../../utils/documentLinks';

/**
 * DocumentTile: the one attachment card of the app. Every document shown,
 * uploaded or picked (a client's ID, a loan's payslip, a file chosen in a
 * form) is this card: the type's icon and colour, its name and file, and the
 * actions along the bottom.
 *
 *  - On file (doc):       View | Download  (+ Replace when onPick is given)
 *  - Picked, not saved:   Replace | Remove
 *  - Not there:           Upload, or "Missing" / "Not uploaded"
 *
 * Props:
 *   type      {string}    the document type, e.g. 'Payslip'
 *   label     {string}    optional title instead of the type
 *   hint      {string}    shown while there is no file, e.g. 'The last three months'
 *   doc       {object}    a stored document: { id, file_name, file_path }
 *   file      {File}      a file picked but not saved yet
 *   onPick    {function}  (File) => void: shows Upload / Replace
 *   onRemove  {function}  removes the picked file
 *   accept    {string}    the file types to pick, PDF by default
 *   uploading {boolean}
 *   required  {boolean}   when missing: a red card and "Missing"
 *   disabled  {boolean}
 *   badge     {string}    a small pill after the title, e.g. 'Client'
 *   onView / onDownload   default: open / download the stored document
 *   viewing   {boolean}   View is showing the document on the page (Hide)
 */

// Each type's icon and colour, the same on every screen.
const LOOKS = [
  { test: (t) => t === 'identification' || t === 'id' || t === 'id document' || /identification|passport|\bid or passport\b/.test(t), Icon: IdCard, tone: 'text-blue-600 dark:text-blue-400' },
  { test: (t) => /proof of (residence|address)|^por$/.test(t), Icon: Home, tone: 'text-purple-600 dark:text-purple-400' },
  { test: (t) => /bank statement|statement/.test(t), Icon: Landmark, tone: 'text-green-600 dark:text-green-400' },
  { test: (t) => /pay ?slip/.test(t), Icon: Receipt, tone: 'text-yellow-600 dark:text-yellow-400' },
  { test: (t) => /contract|agreement/.test(t), Icon: FileSignature, tone: 'text-rose-600 dark:text-rose-400' },
  { test: (t) => /photo|selfie/.test(t), Icon: Camera, tone: 'text-indigo-600 dark:text-indigo-400' },
  { test: (t) => /credit ?report|bureau/.test(t), Icon: ShieldCheck, tone: 'text-teal-600 dark:text-teal-400' },
];
// The names shown for document types stored under a code.
const TYPE_NAMES = {
  ClientSuppliedBureauReport: 'Credit report (client)',
  'Credit Bureau Report': 'Credit bureau report',
};
export const docLook = (type) => {
  const t = String(type || '').trim().toLowerCase();
  return LOOKS.find((l) => l.test(t)) || { Icon: FileText, tone: 'text-gray-500 dark:text-slate-400' };
};

/** The grid the tiles sit in. */
export const DOCUMENT_GRID = 'grid grid-cols-2 md:grid-cols-4 gap-3';

const fileNameOf = (doc) => {
  const name = doc?.file_name || doc?.original_name || doc?.file_path || '';
  return String(name).split(/[\\/]/).pop();
};

const FOOT = 'flex-1 py-1.5 flex items-center justify-center gap-1';

const DocumentTile = ({
  type,
  label,
  hint,
  doc = null,
  file = null,
  onPick,
  onRemove,
  accept = 'application/pdf',
  uploading = false,
  required = false,
  disabled = false,
  badge,
  onView,
  onDownload,
  viewing = false,
}) => {
  const inputId = useId();
  const { Icon, tone } = docLook(type);
  const has = Boolean(doc || file);
  const missing = !has;
  const pickLabel = /image/.test(accept) && !/pdf/.test(accept) ? 'photo' : 'PDF';

  const view = onView || (() => doc?.id && openDocument(doc.id).catch(() => {}));
  const download = onDownload || (() => doc?.id && downloadDocument(doc.id));

  const picker = (text, PickIcon) => (
    <label
      htmlFor={inputId}
      className={`${FOOT} ${disabled ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer'}`}
    >
      <PickIcon size={13} /> {text}
    </label>
  );
  const divider = <div className="border-r border-gray-200 dark:border-slate-600" />;

  let footer;
  if (uploading) {
    footer = (
      <span className={`${FOOT} text-gray-600 dark:text-slate-300 cursor-wait`}>
        <span aria-hidden="true" className="inline-block w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin" /> Uploading…
      </span>
    );
  } else if (file) {
    footer = (
      <>
        {onPick && picker('Replace', RefreshCw)}
        {onPick && onRemove && divider}
        {onRemove && (
          <button type="button" onClick={onRemove} disabled={disabled}
            className={`${FOOT} text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-slate-700 disabled:opacity-50`}>
            <X size={13} /> Remove
          </button>
        )}
      </>
    );
  } else if (doc && !doc.id && !onView) {
    // On file, but not one this page can open (a public link): replace only.
    footer = onPick ? picker('Replace', RefreshCw) : <span className={`${FOOT} text-emerald-700 dark:text-emerald-400`}>On file</span>;
  } else if (doc) {
    footer = (
      <>
        <ActionButton onClick={view} className={`${FOOT} text-blue-600 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-slate-700`}>
          {viewing ? <EyeOff size={13} /> : <Eye size={13} />} {viewing ? 'Hide' : 'View'}
        </ActionButton>
        {divider}
        <ActionButton onClick={download} className={`${FOOT} text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-slate-700`}>
          <Download size={13} /> Download
        </ActionButton>
        {onPick && <>{divider}{picker('Replace', RefreshCw)}</>}
      </>
    );
  } else if (onPick) {
    footer = picker(`Upload ${pickLabel}`, UploadCloud);
  } else {
    footer = (
      <span className={`${FOOT} ${required ? 'text-rose-600 dark:text-rose-400' : 'text-gray-500 dark:text-slate-400'}`}>
        {required ? 'Missing' : 'Not uploaded'}
      </span>
    );
  }

  // Missing and nothing to upload it with: red. A card waiting for an upload
  // looks like any other document card (the Documents Hub's).
  const cardTone = file
    ? 'bg-emerald-50/60 border-emerald-300 dark:bg-emerald-900/10 dark:border-emerald-700/60'
    : missing && required && !onPick
      ? 'bg-rose-50/50 border-rose-200 dark:bg-rose-900/10 dark:border-rose-800/50'
      : 'bg-gray-50 border-gray-200 dark:bg-slate-700/40 dark:border-slate-600';
  const nameLine = file ? file.name : doc ? (fileNameOf(doc) || 'On file') : (hint || 'Not uploaded yet');

  return (
    <div className={`rounded-lg overflow-hidden border flex flex-col transition-shadow hover:shadow-md ${cardTone}`}>
      <div className="flex-1 p-3 flex flex-col items-center text-center gap-1.5">
        <div className="relative">
          <Icon size={34} strokeWidth={1.5} className={missing && !onPick ? 'text-gray-300 dark:text-slate-600' : tone} />
          {has && <CheckCircle2 size={15} className="absolute -right-1.5 -bottom-1 text-emerald-600 bg-white dark:bg-slate-800 rounded-full" />}
        </div>
        <div className="font-medium text-xs text-gray-900 dark:text-slate-100">
          {label || TYPE_NAMES[type] || type}
          {badge && <span className="ml-1.5 align-middle px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">{badge}</span>}
        </div>
        <div className="text-[11px] text-gray-500 dark:text-slate-400 w-full truncate" title={nameLine}>{nameLine}</div>
      </div>
      <div className="flex border-t border-gray-200 dark:border-slate-600 text-[11px] font-medium">
        {footer}
      </div>
      {onPick && (
        <input
          id={inputId}
          type="file"
          accept={accept}
          className="hidden"
          disabled={disabled || uploading}
          onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) onPick(f); e.target.value = ''; }}
        />
      )}
    </div>
  );
};

export default DocumentTile;
